import i18n from '../i18n'
import type { useOnboardingStore } from '../store/onboardingStore'
import type { AddressVerificationStatus } from '../services/api'
import { communicationLanguage } from '../data/patientLanguages'
import { isMinor } from './minor'

type Store = ReturnType<typeof useOnboardingStore.getState>
const VALID_ADDR_STATUS: string[] = ['exact', 'street', 'city_only', 'unverified'] satisfies AddressVerificationStatus[]

/**
 * The ONE place the submission payload is assembled.
 *
 * Two steps create submissions (Step 11 SEPA and Step 12 Complete for orgs
 * that skip SEPA). They used to hold two hand-copied copies of this object;
 * the copies drifted and new fields silently never reached the server for
 * one of the paths. Add fields here and both entry points get them.
 */
export function buildSubmissionPayload(
  store: Pick<Store, 'patientNumber' | 'phoneNumber' | 'personalData' | 'addressData' | 'insuranceData' | 'guardianData'
    | 'consents' | 'italyConsents' | 'sepaDropinCompleted' | 'cardSaved' | 'cardOptOut' | 'addressCheck'
    | 'yousignRequestId' | 'yousignSignerId' | 'uploadedDocuments'>,
  opts: {
    sepaOptOut: boolean
    /** SEPA A/B tests: the arms this patient saw, and the two-step opt-out reason (Step 11 only) */
    sepaVariant?: 'dropin' | 'redirect' | null
    sepaOptOutVariant?: 'control' | 'two_step' | null
    sepaOptOutReason?: string | null
  },
) {
  const {
    patientNumber, phoneNumber, personalData, addressData, insuranceData, guardianData,
    consents, italyConsents, sepaDropinCompleted, cardSaved, cardOptOut, addressCheck,
    yousignRequestId, yousignSignerId, uploadedDocuments,
  } = store
  // Italy: a minor's parents sign the minors consent (ItalyGuardianStep); a Tutore signs alone
  const minorWithParent = guardianData.hasGuardian && guardianData.guardianType === 'Genitore' && isMinor(personalData.birthDate)
  return {
      patientNumber,
      phoneNumber,
      // The language for our e-mails and messages (PSY-1040 → PsyCloud `language`, read by
      // Customer.io): the patient's pick in Step 2, else the form language the dropdown showed.
      language: communicationLanguage(personalData.communicationLanguage, i18n.language),
      personalData: {
        gender: personalData.gender || '',
        firstName: personalData.firstName,
        lastName: personalData.lastName,
        email: personalData.email || '',
        birthDate: personalData.birthDate,
        birthPlace: personalData.birthPlace,
        codiceFiscale: personalData.codiceFiscale || undefined,
        codiceFiscaleAbsent: personalData.codiceFiscaleAbsent || undefined,
        generalPractitionerName: personalData.generalPractitionerName || undefined,
        generalPractitionerPhone: personalData.generalPractitionerPhone || undefined,
        generalPractitionerEmail: personalData.generalPractitionerEmail || undefined,
        idDocumentType: personalData.idDocumentType || undefined,
        idDocumentNumber: personalData.idDocumentNumber || undefined,
        idDocumentIssuedBy: personalData.idDocumentIssuedBy || undefined,
        idDocumentIssuedOn: personalData.idDocumentIssuedOn || undefined,
      },
      addressData: {
        address: addressData.address,
        street: addressData.street || undefined,
        houseNumber: addressData.houseNumber || undefined,
        noHouseNumber: addressData.noHouseNumber || undefined,
        addressLine2: addressData.addressLine2,
        city: addressData.city,
        state: addressData.state,
        province: addressData.province || undefined,
        postalCode: addressData.postalCode,
        country: addressData.country,
        domicileSameAsResidence: addressData.domicileSameAsResidence,
        domicileAddress: addressData.domicileAddress,
        domicileCity: addressData.domicileCity,
        domicileState: addressData.domicileState,
        domicilePostalCode: addressData.domicilePostalCode,
        domicileCountry: addressData.domicileCountry || undefined,
      },
      insuranceData: {
        insuranceType: insuranceData.insuranceType || '',
        insuranceCountry: insuranceData.insuranceCountry || '',
        insuranceProvider: insuranceData.insuranceProvider || '',
        insuranceProviderOther: insuranceData.insuranceProviderOther || '',
      },
      guardianData: {
        hasGuardian: guardianData.hasGuardian,
        guardianName: guardianData.guardianName || '',
        guardianAddress: guardianData.guardianAddress || '',
        guardianScope: guardianData.guardianScope || '',
        guardianDocumentFilename: guardianData.guardianDocumentFilename || '',
        guardianType: guardianData.guardianType || undefined,
        guardianFirstName: guardianData.guardianFirstName || undefined,
        guardianLastName: guardianData.guardianLastName || undefined,
        guardianBirthDate: guardianData.guardianBirthDate || undefined,
        guardianBirthPlace: guardianData.guardianBirthPlace || undefined,
        guardianTaxCode: guardianData.guardianTaxCode || undefined,
        guardianPhone: guardianData.guardianPhone || undefined,
        guardianEmail: guardianData.guardianEmail || undefined,
        guardianStreet: guardianData.guardianStreet || undefined,
        guardianHouseNumber: guardianData.guardianHouseNumber || undefined,
        guardianPostalCode: guardianData.guardianPostalCode || undefined,
        guardianCity: guardianData.guardianCity || undefined,
        // Minor (Italy) with a parent: only the branch the family chose — no leftovers from switching
        // back and forth (type → Tutore, or a corrected birth date that makes the patient an adult)
        ...(minorWithParent && guardianData.guardianSignatures === 'both' ? {
          guardian2FirstName: guardianData.guardian2FirstName?.trim() || undefined,
          guardian2LastName: guardianData.guardian2LastName?.trim() || undefined,
          guardian2Phone: guardianData.guardian2Phone?.trim() || undefined,
          guardian2Email: guardianData.guardian2Email?.trim() || undefined,
        } : {}),
        ...(minorWithParent && guardianData.guardianSignatures === 'single' ? {
          guardianSingleSignatureReason: guardianData.guardianSingleSignatureReason || undefined,
        } : {}),
      },
      consents,
      italyConsents,
      sepaOptOut: opts.sepaOptOut,
      sepaDropinCompleted,
      sepaVariant: opts.sepaVariant || undefined,
      sepaOptOutVariant: opts.sepaOptOutVariant || undefined,
      sepaOptOutReason: opts.sepaOptOutReason || undefined,
      cardSaved,
      cardOptOut,
      addressValidation: addressCheck && VALID_ADDR_STATUS.includes(addressCheck.status) ? addressCheck : null,
      yousignRequestId,
      yousignSignerId,
      uploadedDocuments,
  }
}
